import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { IEquity, IEquityEntry } from '@modeso/types__aumico-lib-annualreport-be';
import { ViewMode } from '@modeso/types__aumico-lib-core-be';
import { IEquityOptions } from '@modeso/types__aumico-lib-notes-be';
import Debug from 'debug';
import { IKeyName } from '../../interfaces/IKeyName.interface';
import { NotesPageBase } from '../NotesPageBase';
import { ITableWithForm } from '../tableWithForm/ITableWithForm.interface';
import { ITableWithFormOptions } from '../tableWithForm/ITableWithFormOptions.interface';
import { IEquityDelegate } from './IEquityDelegate.interface';

const debug = Debug('aumico:aumico-lib-notes-fe:EquityComponent');

@Component({
    selector: 'aumico-notes-equity',
    templateUrl: 'equity.component.html',
    styleUrls: ['equity.component.scss'],
})
export class EquityComponent extends NotesPageBase implements OnInit, OnChanges {
    @Input() componentIndex: number;

    @Input() public set delegate(delegate: IEquityDelegate) {
        if (delegate) {
            this.DELEGATE = delegate;
        }
    }

    totalRowTextCell: string;

    DELEGATE?: IEquityDelegate;
    @Input() editable = true;
    tableOptions: ITableWithFormOptions = {
        properties: [],
    };
    displayedColumns: string[] = [];
    additionalHeaders: IKeyName[] = [];
    dataToDelegate: IEquity;
    // initializing equityData for notes type preview
    EquityData: IEquity = {
        currentDate: '2021-12-31T22:00:00.000Z',
        previousDate: '2020-12-31T22:00:00.000Z',
        currency: 'CHF',
        options: {} as IEquityOptions,
        rows: [],
        totalRow: { text: 'Total', currentValue1: 0, currentValue2: 0, previousValue1: 0, previousValue2: 0 },
    };
    @Input() set equityData(value: IEquity) {
        if (value != null) {
            this.EquityData = { ...value };
            this.totalRowTextCell = this.EquityData.totalRow ? this.EquityData.totalRow.text : undefined;
            this.ComponentOptions = value.options ?? this.EquityData.options;

            this.setTableSettings();
        }
    }
    // Component options is a separate input because notes type form passes this input for preview purposes
    ComponentOptions: IEquityOptions;
    @Input() set componentOptions(value: IEquityOptions) {
        if (value != null) {
            this.ComponentOptions = value;
        } else {
            this.ComponentOptions = this.EquityData.options;
        }
    }

    @Input() public language = 'de-ch';
    @Input() viewMode: ViewMode = ViewMode.EDIT;

    @Input() highlightTodos = false;

    viewModeEnum = ViewMode;
    tableTitle: string;

    @Input() tableNumber: string | undefined;
    // to update component Options when input for equityOptions is passed
    ngOnChanges(change: SimpleChanges) {
        if (change['equityData'] && change['equityData'].currentValue) {
            this.ComponentOptions = change['equityData'].currentValue.options;
        }

        if (change['language']?.currentValue) {
            this.setTableSettings();
        }
    }

    ngOnInit(): void {
        debug('ngOnInit');
        this.setTableSettings();
    }
    onFormChange(form: ITableWithForm) {
        const formData: IEquityEntry[] = form.formValue as IEquityEntry[];
        const totalRowData: IEquityEntry = form.totalRowValue as IEquityEntry;
        let options = this.dataToDelegate?.options || this.ComponentOptions;
        if (form.headers != null) {
            options = {
                ...options,
                textTitle: { [this.language]: form.headers.text },
                currentValueTitle1: { [this.language]: form.headers.currentValue1 },
                currentValueTitle2: { [this.language]: form.headers.currentValue2 },
                previousValueTitle1: { [this.language]: form.headers.previousValue1 },
                previousValueTitle2: { [this.language]: form.headers.previousValue2 },
            };
        }
        this.totalRowTextCell = totalRowData ? totalRowData.text : undefined;

        this.dataToDelegate = {
            currentDate: this.EquityData.currentDate,
            previousDate: this.EquityData.previousDate,
            currency: this.EquityData.currency,
            options,
            rows: formData,
            totalRow: totalRowData,
            addNumbering: this.addNumbering,
            referenceFlis: this.referenceFlis,
        };
        debug('equityData', this.dataToDelegate);
        if (this.DELEGATE) {
            this.DELEGATE.equityComponentData(this.dataToDelegate, this.componentIndex);
        }
    }

    onToggleChange(checked: boolean): void {
        this.ComponentOptions.totalRow = checked;
    }
    onTitleChange(title: string): void {
        const options = this.dataToDelegate?.options || this.ComponentOptions;
        this.dataToDelegate = {
            ...this.dataToDelegate,
            options: {
                ...options,
                textTitle: { [this.language]: title },
            },
        };

        this.updateAdditionalHeaders(title);

        this.DELEGATE.equityComponentData(this.dataToDelegate, this.componentIndex);
    }

    onAddNumberingClicked(): void {
        this.onAddNumberToggleChange(!this.addNumbering);
    }

    onAddReferencingClicked(): void {
        this.openReferenceDialog();
    }

    setTableSettings() {
        this.tableTitle = this.ComponentOptions.textTitle?.[this.language];
        // Names that are empty means that these columns will have no header name in the table.
        this.tableOptions.properties = [
            {
                name: this.tableTitle,
                type: 'text',
                property: 'text',
                required: true,
                flex: 30,
                headerType: 'string',
                hasReference: true,
            },
            {
                name: this.ComponentOptions.currentValueTitle1[this.language],
                type: 'amount',
                property: 'currentValue1',
                required: false,
                hasTotal: true,
                highlighted: true,
                flex: 10,
                disabled: false,
            },
            {
                name: this.ComponentOptions.currentValueTitle2[this.language],
                type: 'amount',
                property: 'currentValue2',
                required: false,
                hasTotal: true,
                highlighted: true,
                flex: 10,
                disabled: false,
            },
            {
                name: this.ComponentOptions.previousValueTitle1[this.language],
                type: 'amount',
                property: 'previousValue1',
                required: false,
                hasTotal: true,
                flex: 10,
                disabled: false,
            },
            {
                name: this.ComponentOptions.previousValueTitle2[this.language],
                type: 'amount',
                property: 'previousValue2',
                required: false,
                hasTotal: true,
                flex: 10,
                disabled: false,
            },
            { name: '', type: 'remove', property: 'remove', required: false, flex: 1 },
        ];
        this.tableOptions.addNewItemText = 'aumico_notes_table_with_form_add_new_item';

        this.tableOptions = { ...this.tableOptions };

        this.displayedColumns = [
            'text',
            'currentValue1',
            'currentValue2',
            'previousValue1',
            'previousValue2',
            'remove',
        ];
        // Additional Headers are a row of headers on top of the basic table headers
        // Names that are empty means that these columns will have no additional header name in the table.
        this.additionalHeaders = [
            {
                key: 'textTitle',
                name: '',
                type: 'title',
                flex: 30,
            },
            {
                key: 'currentYear',
                name: this.EquityData.currentDate,
                type: 'date',
                highlighted: true,
                flex: 20,
            },
            {
                key: 'previousYear',
                name: this.EquityData.previousDate,
                type: 'date',
                flex: 20,
            },
            {
                key: 'options',
                name: '',
                type: 'hidden',
                flex: 1,
            },
        ];
    }

    private updateAdditionalHeaders(title: string): void {
        this.tableTitle = title;
    }
}
